import { RecordingStatus } from '@heidi/schema/ml-scribe';

import type { WorkspaceRecordingState } from './workspace.types';

/**
 * Collapse the FE's fragmented recording signals into one tri-state for
 * the workspace snapshot.
 *
 * Inputs:
 *   - ``activeSessionId``: ``activeSessionIdAtom`` — the synchronous,
 *     local-only "recording is happening RIGHT NOW in this tab" signal.
 *     It is cleared on BOTH pause and stop (see ``liveTranscriptionAtom``),
 *     so its non-null presence is the only trustworthy "recording" tell.
 *   - ``subjectSessionId``: the session the snapshot reports recording for
 *     (the open session artifact, or the actively-recording session).
 *   - ``serverStatus``: the session's persisted ``recording_status``. Used
 *     only to distinguish ``paused`` from ``idle`` once the live atom is
 *     cleared — server ``RECORDING`` is an optimistic local write that a
 *     background refetch can silently revert, so it never upgrades to
 *     ``recording`` on its own.
 *
 * This is what fixes the post-refresh desync: after a refresh the live
 * atom is null and the server reports ``IDLE``/``PAUSED``, so the agent
 * is told ``idle``/``paused`` even if it still believes it is recording.
 */
export function deriveRecordingState(
  activeSessionId: string | null,
  subjectSessionId: string | undefined,
  serverStatus: RecordingStatus | null | undefined
): WorkspaceRecordingState {
  if (subjectSessionId && activeSessionId === subjectSessionId) {
    return 'recording';
  }
  if (serverStatus === RecordingStatus.PAUSED) {
    return 'paused';
  }
  return 'idle';
}
